// web/src/components/rtls/AddLocationDialog.tsx
//
// Modal dialog for adding OR editing a location. Pre-fills x/y/z from the
// last map tap (uiSlice.tappedCoord) in add mode; in edit mode the form
// is pre-filled from the existing row in Redux. On submit: optimistic
// dispatch + Supabase INSERT/UPDATE. Errors show a toast and rollback.

import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useAppDispatch } from '../../app/useAppDispatch'
import { useAppSelector } from '../../app/useAppSelector'
import { rtlsActions } from '@shared/slices/rtlsSlice'
import { uiActions } from '@shared/slices/uiSlice'
import { supabase } from '../../app/supabaseClient'
import type { LocationType, LocationData } from '@shared/types/rtlsTypes'
import { showToast } from '../Toast'

const TYPE_OPTIONS: LocationType[] = [
  'entry', 'tunnel', 'station', 'safety', 'infrastructure', 'stope', 'surface',
]

interface AddLocationDialogProps {
  open: boolean
  onClose: () => void
  // When set, the dialog operates in edit mode: form prefills from the
  // existing row and submit triggers UPDATE instead of INSERT.
  editingId?: string | null
}

export const AddLocationDialog: React.FC<AddLocationDialogProps> = ({
  open,
  onClose,
  editingId = null,
}) => {
  const dispatch = useAppDispatch()
  const tappedCoord     = useAppSelector(s => s.ui.tappedCoord)
  const mineId          = useAppSelector(s => s.map.mineId)
  const locationsRecord = useAppSelector(s => s.rtls.locations)

  const isEditing = !!editingId

  const [name, setName] = useState('')
  const [type, setType] = useState<LocationType>('station')
  const [x, setX] = useState('0')
  const [y, setY] = useState('0')
  const [z, setZ] = useState('0')
  const [level, setLevel] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Initial prefill on dialog open. Edit mode reads from Redux; add mode
  // uses the last map tap if present. Deliberately NOT reactive to
  // tappedCoord changes — mid-session taps are handled by a separate
  // effect below so we don't stomp on user edits.
  useEffect(() => {
    if (!open) return
    if (editingId) {
      const loc = locationsRecord[editingId]
      if (loc) {
        setName(loc.name)
        setType(loc.type)
        setX(loc.position.x.toFixed(2))
        setY(loc.position.y.toFixed(2))
        setZ(loc.position.z.toFixed(2))
        setLevel(loc.level != null ? String(loc.level) : '')
      }
      return
    }
    if (tappedCoord) {
      setX(tappedCoord.x.toFixed(2))
      setY(tappedCoord.y.toFixed(2))
      setZ(tappedCoord.z.toFixed(2))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId])

  // Mid-session tap while the dialog is open re-positions x/y/z in either
  // mode. Fires only when tappedCoord actually changes.
  useEffect(() => {
    if (!open || !tappedCoord) return
    setX(tappedCoord.x.toFixed(2))
    setY(tappedCoord.y.toFixed(2))
    setZ(tappedCoord.z.toFixed(2))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tappedCoord])

  // Reset form + clear tappedCoord when the dialog closes.
  useEffect(() => {
    if (!open) {
      setName('')
      setType('station')
      setLevel('')
      setSubmitting(false)
      dispatch(uiActions.setTappedCoord(null))
    }
  }, [open, dispatch])

  if (!open) return null

  const trimmedName = name.trim()
  const xNum = parseFloat(x)
  const yNum = parseFloat(y)
  const zNum = parseFloat(z)
  const levelNum = level.trim() ? parseInt(level, 10) : undefined
  const canSubmit = !!trimmedName && [xNum, yNum, zNum].every(Number.isFinite) && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)

    if (isEditing && editingId) {
      // ── Edit mode — UPDATE with optimistic dispatch + rollback ────────
      const prev = locationsRecord[editingId]
      if (!prev) {
        showToast('Cannot edit: location no longer exists.', 'error')
        onClose()
        return
      }
      const updated: LocationData = {
        ...prev,
        name: trimmedName,
        type,
        position: { x: xNum, y: yNum, z: zNum },
        level: levelNum,
      }
      dispatch(rtlsActions.updateLocation(updated))
      onClose()

      const { error } = await supabase
        .from('locations')
        .update({
          name: trimmedName,
          type,
          x: xNum, y: yNum, z: zNum,
          level: levelNum ?? null,
        })
        .eq('id', editingId)
      if (error) {
        dispatch(rtlsActions.updateLocation(prev))
        showToast(`Failed to save: ${error.message}`, 'error')
      } else {
        showToast('Location updated.', 'success')
      }
      return
    }

    // ── Add mode — INSERT with optimistic dispatch + rollback ───────────
    const id = crypto.randomUUID()
    const newLocation: LocationData = {
      id,
      name: trimmedName,
      type,
      position: { x: xNum, y: yNum, z: zNum },
      level: levelNum,
      mine_id: mineId ?? undefined,
    }

    dispatch(rtlsActions.addLocation(newLocation))
    onClose()

    if (!mineId) {
      dispatch(rtlsActions.removeLocation(id))
      showToast('Cannot save: no mine loaded. Check Supabase bootstrap.', 'error')
      return
    }

    const { error } = await supabase.from('locations').insert({
      id,
      mine_id: mineId,
      name: trimmedName,
      type,
      x: xNum, y: yNum, z: zNum,
      level: levelNum ?? null,
      created_by: null,
    })
    if (error) {
      dispatch(rtlsActions.removeLocation(id))
      showToast(`Failed to save location: ${error.message}`, 'error')
    } else {
      showToast('Location added.', 'success')
    }
  }

  const fieldCls = 'w-full rounded-sm border border-surfaceBorder bg-surfaceCard px-sm py-xs text-sm text-textPrimary focus:border-primary focus:outline-none'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isEditing ? 'Edit location' : 'Add location'}
      onClick={onClose}
    >
      <div
        className="w-96 rounded-md border border-surfaceBorder bg-surface p-lg shadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="mb-md text-lg font-semibold text-textPrimary">
          {isEditing ? 'Edit location' : 'Add location'}
        </h2>

        <div className="flex flex-col gap-sm">
          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Name
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              className={fieldCls}
              placeholder="Refuge Chamber A2"
            />
          </label>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Type
            <select
              value={type}
              onChange={e => setType(e.target.value as LocationType)}
              className={fieldCls}
            >
              {TYPE_OPTIONS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-3 gap-sm">
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              X
              <input type="text" inputMode="decimal" value={x} onChange={e => setX(e.target.value)} className={fieldCls} />
            </label>
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              Y
              <input type="text" inputMode="decimal" value={y} onChange={e => setY(e.target.value)} className={fieldCls} />
            </label>
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              Z
              <input type="text" inputMode="decimal" value={z} onChange={e => setZ(e.target.value)} className={fieldCls} />
            </label>
          </div>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Level (optional)
            <input
              type="text"
              inputMode="numeric"
              value={level}
              onChange={e => setLevel(e.target.value)}
              className={fieldCls}
              placeholder="e.g. 2"
            />
          </label>

          {tappedCoord && (
            <p className="text-xs text-textMuted">
              Tip: click a point on the 3D map to pre-fill X/Y/Z.
            </p>
          )}
        </div>

        <div className="mt-lg flex justify-end gap-sm">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm px-md py-xs text-sm text-textMuted hover:text-textPrimary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'rounded-sm px-md py-xs text-sm font-semibold transition-colors',
              canSubmit
                ? 'bg-primary text-black hover:bg-primaryDark'
                : 'cursor-not-allowed bg-surfaceCard text-textDisabled',
            )}
          >
            {submitting ? 'Saving...' : isEditing ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  )
}
